// SPDX-License-Identifier: Apache-2.0
import Feature from 'ol/Feature';
import { GeoJSON } from 'ol/format';
import GirafeSingleton from '../../base/GirafeSingleton';
import IGirafeContext from '../context/icontext';
import { Extent } from 'ol/extent';
import { createBufferedExtentFromFeature } from '../geometrytools';

export type BaseSearchClientConfig = {
  type: string;
  providerName: string;
  url: string;
  resultSrid: string;
};

// The default provider is a container for search results that do not result from a provider search (e.g. map re-centering)
export const SEARCH_PROVIDER_DEFAULT = 'default';
// The default search group contains search results that do not belong to a specific category, they are listed without a list header
export const SEARCH_GROUP_DEFAULT = 'default';
// The map recenter search group contains the coordinates for re-centering the map
export const SEARCH_GROUP_RECENTER_MAP = 'recenter_map';

abstract class AbstractSearchClient extends GirafeSingleton {
  public readonly type: string;
  public readonly providerName: string;
  protected url: URL;
  protected readonly resultSrid;
  protected readonly searchTermPlaceholder = '###SEARCHTERM###';
  protected readonly searchLangPlaceholder = '###SEARCHLANG###';
  protected searchTermQueryParameter: string | undefined;
  protected searchLangQueryParameter: string | undefined;
  protected searchFilterQueryParameter: string | undefined;
  protected readonly geoJsonFormatter = new GeoJSON();

  protected constructor(context: IGirafeContext, config: BaseSearchClientConfig) {
    super(context);
    this.type = config.type;
    this.providerName = config.providerName;
    this.url = this.sanitizeUrl(config.url);
    this.resultSrid = config.resultSrid;
  }

  public override async initializeSingleton() {
    await super.initializeSingleton();
  }

  protected get mapProjection() {
    return this.context.mapManager.getMap().getView().getProjection();
  }

  /**
   * Sanitize the url by removing placeholders that are wrongfully interpreted as URL fragments by the URL parser.
   * The parameters are introduced again and set when fetching the search results.
   */
  private sanitizeUrl(url: string): URL {
    // First, replace problematic characters in the placeholders and url that cause issues with the URL parser
    const sanitizedSearchTermPlaceholder = this.sanitizePlaceholder(this.searchTermPlaceholder);
    const sanitizedSearchLangPlaceholder = this.sanitizePlaceholder(this.searchLangPlaceholder);
    const sanitizedUrl = new URL(this.sanitizePlaceholder(url));
    // Then, save and remove the query parameters that contain placeholders; they'll be introduced again when fetching the search results
    this.searchTermQueryParameter = this.extractQueryParamWithPlaceholder(sanitizedUrl, sanitizedSearchTermPlaceholder);
    this.searchLangQueryParameter = this.extractQueryParamWithPlaceholder(sanitizedUrl, sanitizedSearchLangPlaceholder);
    return sanitizedUrl;
  }

  private extractQueryParamWithPlaceholder(url: URL, sanitizedPlaceholder: string): string | undefined {
    const queryParameter = Array.from(url.searchParams.entries()).find(
      (entry) => entry[1] === sanitizedPlaceholder
    )?.[0];
    if (queryParameter) {
      url.searchParams.delete(queryParameter);
    }
    return queryParameter;
  }

  private sanitizePlaceholder(urlOrPlaceholder: string): string {
    // Replace ### type placeholders because they are interpreted as URL fragments
    const placeholderMarker = '###';
    const urlSafePlaceholderMarker = '_';
    return urlOrPlaceholder.replaceAll(placeholderMarker, urlSafePlaceholderMarker);
  }

  /**
   * Returns a list of categories that can be searched by.
   */
  public async getSearchCategories(): Promise<string[]> {
    return [];
  }

  protected buildRequestUrl(term: string, filter: string[]): URL {
    const url = new URL(this.url);
    if (this.searchTermQueryParameter) {
      url.searchParams.set(this.searchTermQueryParameter, term);
    }
    if (this.searchLangQueryParameter && this.context.stateManager.state.language) {
      url.searchParams.set(this.searchLangQueryParameter, this.context.stateManager.state.language);
    }
    if (this.searchFilterQueryParameter && filter.length) {
      url.searchParams.set(this.searchFilterQueryParameter, filter.join(','));
    }
    return url;
  }

  /**
   * Executes a search request with the provided search term.
   */
  public async requestSearch(term: string, filter: string[], abortController: AbortController): Promise<Feature[]> {
    const url = this.buildRequestUrl(term, filter);
    try {
      const response = await fetch(url, { signal: abortController.signal });
      return await this.parseResponse(response);
    } catch (error) {
      console.error(`Error fetching search results from ${this.url.toString()}:`, error);
      return [];
    }
  }

  /**
   * Parses the response from the server and returns an array of features.
   * Features must have the following properties to be used correctly with the search component:
   * - `provider`
   * - `label`
   * - `label_html`: optional, for styled entries in the search results list
   * - `actions`: optional, a list of SearchResultsActions
   */
  protected async parseResponse(response: Response): Promise<Feature[]> {
    const data = (await response.json()) as GeoJSON;

    const features = this.geoJsonFormatter.readFeatures(data, {
      dataProjection: this.resultSrid,
      featureProjection: this.mapProjection
    });

    return features.map((f) => {
      f.set('provider', this.providerName);
      return f;
    });
  }

  /**
   * Groups the results by the default group, which equivalates to no grouping. Should be overridden by the client class
   * to group results by a custom criteria like search category.
   */
  public groupedResults(results: Feature[]): Record<string, Feature[]> {
    return { [SEARCH_GROUP_DEFAULT]: results };
  }

  /**
   * Returns a bbox around the geometry that is 50% larger than the geometry.
   */
  public getZoomToBbox(feature: Feature): Extent | undefined {
    return createBufferedExtentFromFeature(feature);
  }

  /**
   * Retrieves the translation key for a given category. Default: use category string directly.
   */
  public getI18nKeyForCategory(category: string): string {
    return category;
  }
}

export default AbstractSearchClient;
